import type { NotificationModel } from '@/shared/types/notifications';

/**
 * Dedupe a flattened notification list by `id` (first occurrence wins) and
 * sort descending by `createdOn`.
 *
 * Mirrors Flutter `NotificationCenterBloc.updateNotifications`
 * (`notification_center_bloc.dart:90-97`): as each page arrives the bloc
 * appends only ids it hasn't seen (`firstWhereOrNull((e) => e.id == element.id)`)
 * then re-sorts the whole list `b.createdOn.compareTo(a.createdOn)`.
 *
 * React Query's `useInfiniteQuery` does NOT dedupe across pages — when a new
 * notification arrives mid-session it shifts the server-side page boundaries,
 * so the same row can land on two fetched pages. Flattening `data.pages`
 * then produces duplicate `id`s, which collide on React's `key` and render a
 * doubled row. Server order is also not guaranteed once pages interleave.
 * This helper restores the Flutter invariant at the consumption point.
 *
 * `createdOn` is an ISO-8601 string; lexical comparison on that format is
 * chronological, matching Dart's `DateTime.compareTo` ordering. Rows with a
 * null/empty `createdOn` sort last (Flutter force-unwraps `createdOn!`, so it
 * never expects null here — we degrade gracefully instead of throwing).
 */
export function dedupeAndSortNotifications(
  rows: NotificationModel[],
): NotificationModel[] {
  const seen = new Set<string>();
  const deduped: NotificationModel[] = [];
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    deduped.push(row);
  }
  return deduped.sort((a, b) => {
    const aKey = a.createdOn ?? '';
    const bKey = b.createdOn ?? '';
    if (aKey === bKey) return 0;
    // Descending: newest (largest ISO string) first; empties sink to bottom.
    if (!aKey) return 1;
    if (!bKey) return -1;
    return bKey < aKey ? -1 : 1;
  });
}
